import React, { useState, useEffect, useCallback } from "react";
import { X, FileText, Info } from "lucide-react";
import {
  getProjectName,
  CreateExternalTicket,
} from "../Service/ReportIssueService";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import SidebarHeaderImage from "../../../assests/SidebarHeaderImage.png";

const ReportIssue = ({ onClose }) => {
  const projectUID = "9B6383C9-8BB3-4BAA-BA4B-77750A61B681";
  const navigate = useNavigate();

  const [newTicket, setNewTicket] = useState({
    Title: "",
    Description: "",
    TicketType: 1,
    Severity: 1,
    ProjectUID: "",
  });

  const [attachments, setAttachments] = useState([]);
  const [alert, setAlert] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [createdByEmail, setCreatedByEmail] = useState("");

  /* CLOSE HANDLER*/
  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }

    navigate(-1);
  };

  const fetchProjectName = useCallback(async () => {
    try {
      const res = await getProjectName();

      if (res.data?.status === "Fail" || res.data?.status === "Success") {
        setProjectName(res.data.projectName || "");
      }
    } catch (err) {
      if (err.response?.status === 400) {
        navigate("/bad-request", {
          state: {
            error: err.response?.data?.message || "Bad request (400)",
          },
        });
        return;
      }
      setAlert({
        type: "error",
        message: "Unable to fetch project name",
      });
    }
  }, [navigate]);

  useEffect(() => {
    fetchProjectName();

    setNewTicket((prev) => ({
      ...prev,
      ProjectUID: projectUID,
    }));
  }, [fetchProjectName]);

  useEffect(() => {
    const loadEmployee = async () => {
      try {
        const token = Cookies.get("JWTToken");

        if (!token) return;

        const { UserEmail, email, Email } = jwtDecode(token);
        setCreatedByEmail(UserEmail || email || Email || "");
      } catch (err) {
        console.error(err);
      }
    };

    loadEmployee();
  }, []);

  useEffect(() => {
    if (!alert) return undefined;

    const timeoutId = window.setTimeout(() => setAlert(null), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [alert]);

  /* INPUT HANDLING  */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTicket((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const err = {};
    if (!newTicket.Title.trim()) err.Title = "Title is required";
    if (!newTicket.Description.trim())
      err.Description = "Description is required";
    if (!newTicket.TicketType) err.TicketType = "Ticket Type is required";
    if (!newTicket.Severity) err.Severity = "Severity is required";
    if (!newTicket.ProjectUID) err.ProjectUID = "Project is required";
    return err;
  };

  /* FILE HANDLING  */
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) return;

    if (attachments.length + files.length > 5) {
      setAlert({
        type: "error",
        message: "You can upload a maximum of 5 attachments.",
      });
      e.target.value = "";
      return;
    }

    // Allowed MIME types
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    // Backend size limits
    const sizeLimits = {
      "image/jpeg": 200 * 1024,
      "image/png": 200 * 1024,
      "application/pdf": 5 * 1024 * 1024,
      "application/msword": 2 * 1024 * 1024,
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        2 * 1024 * 1024,
      "application/vnd.ms-excel": 2 * 1024 * 1024,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        2 * 1024 * 1024,
    };

    for (const file of files) {
      if (
        !allowedTypes.includes(file.type) ||
        file.size > sizeLimits[file.type]
      ) {
        setAlert({
          type: "error",
          message:
            "Invalid attachment. Allowed file types and sizes: Image(JPEG, PNG) <= 200 KB, Word <= 2 MB, Excel <= 2MB, PDF <= 5 MB.",
        });
        e.target.value = "";
        return;
      }
    }

    setAttachments((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };
  /*SUBMIT */
  const handleAddTicket = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);

      if (!createdByEmail) {
        setAlert({
          type: "error",
          message: "User email not found. Please login again.",
        });
        setLoading(false);
        return;
      }
      const formData = new FormData();

      Object.keys(newTicket).forEach((key) =>
        formData.append(key, newTicket[key]),
      );
      formData.append("Email", createdByEmail);
      attachments.forEach((file) => formData.append("File", file));
      const response = await CreateExternalTicket(formData);
      if (response.data?.status === "Success") {
        CustomToast("success", response.data?.message || "Ticket created successfully");
        setTimeout(() => handleClose(), 2000);
      } else {
        CustomToast("fail", response.data?.message || "Failed to create ticket");
      }
    } catch (error) {
      if (error.response?.status === 400) {
        CustomToast("fail", error.response?.data?.message || error.response?.data?.Message || "Something went ");
      } else {
        CustomToast("error", error.response?.data?.message || error.response?.data?.Message || "Server error");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${onClose ? "h-full min-h-0" : "min-h-screen"} flex flex-col bg-white`}>
      <div className="relative shrink-0 overflow-hidden border-b bg-[#7f35b2] px-6 py-5">
        <div
          className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${SidebarHeaderImage})` }}
        />
        <div className="pointer-events-none absolute inset-0 bg-black/35" />
        <div className="relative z-10 flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="shrink-0 rounded-xl bg-[#7f35b2]/30 p-2.5">
              <FileText className="text-white" size={22} />
            </div>
            <div className="min-w-0">
              <h2 className="text-2xl font-semibold text-gray-100">Report Issue</h2>
              <p className="mt-0.5 text-sm text-gray-100">Tell us what went wrong and we’ll look into it.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="flex shrink-0 items-center justify-center rounded-lg p-2 text-gray-100 transition hover:bg-white/20 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/70"
            aria-label="Close report issue form"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {/* Title */}
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="Title"
              value={newTicket.Title}
              onChange={handleInputChange}
              className={`mt-1 w-full rounded-xl border px-3 py-2 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:ring-1 ${errors.Title
                ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                : "border-gray-200 focus:border-[#7f35b2] focus:ring-[#7f35b2]/30"
                }`}
              placeholder="Enter ticket title"
            />
            {errors.Title && (
              <p className="text-xs text-red-500 mt-1">{errors.Title}</p>
            )}
          </div>

          {/* Description */}
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="Description"
              value={newTicket.Description}
              onChange={handleInputChange}
              rows={4}
              className={`mt-1 w-full rounded-xl border px-3 py-2 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:ring-1 ${errors.Description
                ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                : "border-gray-200 focus:border-[#7f35b2] focus:ring-[#7f35b2]/30"
                }`}
              placeholder="Enter ticket description"
            />
            {errors.Description && (
              <p className="text-xs text-red-500 mt-1">
                {errors.Description}
              </p>
            )}
          </div>

          {/* Ticket Type */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Ticket Type <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value="Bug"
              disabled
              className="mt-1 w-full cursor-not-allowed rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500"
            />
            {errors.TicketType && (
              <p className="text-xs text-red-500 mt-1">
                {errors.TicketType}
              </p>
            )}
          </div>

          {/* Severity */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Severity <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value="High"
              disabled
              className="mt-1 w-full cursor-not-allowed rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500"
            />
            {errors.Severity && (
              <p className="text-xs text-red-500 mt-1">{errors.Severity}</p>
            )}
          </div>

          {/* Project */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Project Name <span className="text-red-500">*</span>
            </label>

            <input
              type="text"
              value={projectName}
              disabled
              className="mt-1 w-full cursor-not-allowed rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500"
            />

            {errors.ProjectUID && (
              <p className="text-xs text-red-500 mt-1">
                {errors.ProjectUID}
              </p>
            )}
          </div>

          {/* Attachment */}
          <div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Attachment
              </label>

              {/* Info Icon with Tooltip */}
              <div className="relative group">
                <Info
                  size={16}
                  className="cursor-help text-gray-400 hover:text-[#7f35b2]"
                  title="Up to 5 files: JPEG or PNG up to 200 KB, Word or Excel up to 2 MB, and PDF up to 5 MB."
                />
              </div>
            </div>

            <input
              type="file"
              name="File"
              onChange={handleFileChange}
              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx"
              multiple
              className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 file:mr-3 file:rounded-lg file:border-0 file:bg-[#7f35b2]/10 file:px-2.5 file:py-1 file:text-xs file:font-semibold file:text-[#7f35b2] hover:file:bg-[#7f35b2]/15"
            />
          </div>

          {/* Attachment Preview List */}
          {attachments.length > 0 && (
            <div className="col-span-2 mt-1">
              <h3 className="mb-2 text-sm font-semibold text-gray-900">
                Attachments ({attachments.length})
              </h3>

              <div className="max-h-48 w-full overflow-y-auto rounded-xl border border-gray-100 bg-gray-50 p-3">
                {attachments.map((file, index) => (
                  <div
                    key={`${file.name}-${file.lastModified}`}
                    className="mb-2 flex items-center justify-between rounded-xl border border-gray-100 bg-white p-2.5 last:mb-0"
                  >
                    {/* File Preview */}
                    <div className="flex items-center gap-3">
                      {file.type.startsWith("image/") ? (
                        <img
                          src={URL.createObjectURL(file)}
                          className="h-12 w-12 rounded-lg border border-gray-100 object-cover"
                          alt="preview"
                        />
                      ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#7f35b2]/10 text-sm text-[#7f35b2]">
                          {file.name.split(".").pop().toUpperCase()}
                        </div>
                      )}

                      {/* File Info */}
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>

                    {/* Remove */}
                    <button
                      onClick={() => removeAttachment(index)}
                      className="rounded-lg px-2 py-1 text-sm font-medium text-red-600 transition hover:bg-red-50 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-col justify-end gap-3 border-t border-gray-100 bg-white px-6 py-4 sm:flex-row">
        <button
          type="button"
          onClick={handleClose}
          className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#7f35b2]/30 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={loading}
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={handleAddTicket}
          disabled={loading}
          className="flex items-center justify-center gap-2 rounded-xl bg-[#7f35b2] px-4 py-2.5 text-sm font-medium text-white transition hover:bg-[#9e0085] focus:outline-none focus:ring-2 focus:ring-[#7f35b2]/40 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? "Creating..." : "Report Issue"}
        </button>
      </div>

      {alert && (
        <div
          className={`fixed bottom-4 right-4 z-[110] max-w-sm rounded-md px-4 py-3 text-sm text-white shadow-lg ${alert.type === "success" ? "bg-green-600" : "bg-red-600"
            }`}
          role="alert"
        >
          {alert.message}
        </div>
      )}
    </div>
  );
};

export default ReportIssue;

